import type { IComponentController, IScope } from "angular";
import { isFunction } from "angular";
import type { IProfileService } from "App/Modules/Auth";
import type {
    IConfigurationGroup,
    IResponseWithLoading,
    ISchedule,
    IScheduleException,
    ISite,
} from "App/Modules/Models";
import { ScheduleExceptionFunctions } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IHumanizeDurationService, IModalService } from "App/Modules/Services";
import type { Moment } from "moment-timezone";
import { utc } from "moment-timezone";

export default class ScheduleExceptionsComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmModalService",
        "mmProfileService",
        "irHumanizeDurationService",
        "mmSitesRepository",
        "$translate",
    ];

    public exceptionTypes: Array<{ key: string; value: boolean }>;

    public schedule: ISchedule;

    public historicalExceptions: IResponseWithLoading<IScheduleException[]>;

    public site: ISite;

    public configurationGroup: IConfigurationGroup;

    public groupTypeName: string;

    public customHeader: string;

    public hideHints: boolean;

    public changed?: () => void;

    public isDisabled: boolean;

    public showHistoricalExceptions: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly modalService: IModalService,
        private readonly profileService: IProfileService,
        private readonly humanizeDuration: IHumanizeDurationService,
        private readonly sitesRepository: ISitesRepository,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public $onInit() {
        this.schedule.scheduleExceptions =
            this.schedule.scheduleExceptions.sort((a, b) => b.dtStart.valueOf() - a.dtStart.valueOf()) || [];

        this.exceptionTypes = [
            {
                key: this.$translate.instant("Gui.Common.Active"),
                value: true,
            },
            {
                key: this.$translate.instant("Gui.Common.Inactive"),
                value: false,
            },
        ];

        if (this.site && this.schedule) {
            ScheduleExceptionFunctions.enhanceScheduleExceptions(this.schedule.scheduleExceptions, this.site.timeZone);
        }
    }

    public add() {
        const exception: IScheduleException = {
            dtStart: null,
            dtStop: null,
            isOnType: true,
        } as IScheduleException;

        this.schedule.scheduleExceptions.push(exception);

        this.callback();
    }

    public delete(exception: IScheduleException) {
        this.schedule.scheduleExceptions.splice(this.schedule.scheduleExceptions.indexOf(exception), 1);

        this.callback();
    }

    public isEnded(exception: IScheduleException): boolean {
        const utcNow: Moment = utc();
        return exception.dtStop ? exception.dtStop.isSameOrBefore(utcNow) : false;
    }

    public daySchedule(timePeriod: Moment) {
        const clone = timePeriod.clone().tz(this.site.timeZone);
        return this.schedule.weeklySchedule.daySchedules[clone.isoWeekday() - 1];
    }

    public isExceptionStarted(scheduleException: IScheduleException): boolean {
        return (
            scheduleException &&
            scheduleException.dtStart &&
            scheduleException.id &&
            scheduleException.dtStart.isSameOrBefore(this.getToday())
        );
    }

    public getToday(): Moment {
        return utc().tz(this.site.timeZone);
    }

    public getDay(datetime: Moment): string {
        return datetime.format("YYYY-MM-DD");
    }

    public getTime(datetime: Moment): string {
        return datetime.format("HH:mm");
    }

    public callback() {
        if (isFunction(this.changed)) {
            this.changed();
        }
    }
}
